import type { SiteMotion } from "@/lib/motion/site-runtime";

/** Cap total scroll applied in one animation frame (paces wheel bursts + trackpad flicks). */
const MAX_FRAME_VH = 0.135;
const MAX_FRAME_PX = 96;
/**
 * Ceiling on undrained input. One section of travel — past that the queue is
 * the user's intent to skip, which is exactly what one-section gating prevents.
 */
const MAX_PENDING_VH = 0.55;
/** Idle gap that ends a wheel gesture (trackpad momentum tails are shorter). */
const GESTURE_IDLE_MS = 140;
/** After a section timeline unlocks, hold input briefly so the next gate cannot fire immediately. */
const SETTLE_COOLDOWN_MS = 180;
/**
 * Desktop wheel → scrub sensitivity. Slight gain so a small flick advances
 * pinned timelines a bit more; keep tiny so section gates stay one-gesture.
 * Touch path is unchanged (1:1 + fling).
 */
const WHEEL_GAIN = 1.12;

/**
 * Touch flick inertia. `preventDefault` on touchmove kills the browser's own
 * momentum, so a 1:1 drag only ever moved one finger-length of the 90–130vh
 * section runways — reaching the next section took a dozen swipes. Project the
 * release velocity with exponential decay to restore the fling.
 *
 * Finger deltas are coalesced to rAF (not applied mid-event) so ScrollSmoother
 * updates once per frame. Fling uses a softer decay than the first pass so the
 * coast eases instead of slamming into gate locks.
 */
const FLICK_MIN_V = 0.15;
const FLICK_MAX_V = 4.2;
const FLICK_GAIN = 1.55;
const FLICK_DECAY = 0.955;
const FLICK_STOP_V = 0.04;
/** Release only flings if the finger was still moving this recently. */
const FLICK_RELEASE_MS = 100;
/** Touch settle after unlock — short so consecutive swipes feel live. */
const TOUCH_SETTLE_COOLDOWN_MS = 50;

function wheelDeltaY(e: WheelEvent) {
  let dy = e.deltaY;
  if (e.deltaMode === 1) dy *= 16;
  if (e.deltaMode === 2) dy *= window.innerHeight;
  return dy;
}

function applyScroll(m: SiteMotion, dy: number) {
  if (!dy) return;
  const smoother = m.smoother;
  if (smoother) {
    smoother.scrollTop(smoother.scrollTop() + dy);
    return;
  }
  window.scrollBy(0, dy);
}

function spineBusy(m: SiteMotion) {
  return m.scrollLocked || m.sectionTlActive();
}

function splashBlocking(m: SiteMotion) {
  if (m.seekMode || m.resizing) return true;
  if (m.body?.classList.contains("splash-lock")) return true;
  /* Boot loader covers the page — lock failsafe must not leave wheel live. */
  return (
    !!m.body?.classList.contains("progress") &&
    !m.body?.classList.contains("site-ready")
  );
}

function allowsNativeScroll(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  if (target.closest("[data-native-scroll]")) return true;
  const contact = target.closest("#contact");
  return !!(contact && contact.classList.contains("is-active"));
}

/**
 * Own wheel + touch so flicks cannot jump past section `et()` gates:
 * - while a section timeline owns the page (or settle cooldown): swallow
 * - after a gate advances during this gesture: swallow the rest of the flick
 * - otherwise: preventDefault, queue the delta, pay it out at a capped rate
 *
 * Critical: swallowed ticks must NOT refresh the gesture idle timer. Doing so
 * kept a spent gesture alive forever while the user kept scrolling, so they
 * could not leave Hero (or any section) without a full pause.
 *
 * Touch is driven here deliberately — ScrollSmoother keeps `normalizeScroll:
 * false` (scrollbar flicker) and `smoothTouch: false`, so lock()/paused does
 * not pace native touch. Do not re-enable normalizeScroll.
 *
 * Touch deltas apply 1:1 while the finger is down, then a decaying fling
 * carries the release (see FLICK_* above). Wheel keeps the paced queue.
 * One-section spend only applies to wheel — touch would die at the first mark.
 */
export function bindScrollThrottle(m: SiteMotion) {
  let pending = 0;
  let frame = 0;
  let gestureIdleTimer: ReturnType<typeof setTimeout> | undefined;
  let settleUntil = 0;
  let wasBusy = false;
  let touchY = 0;
  let touchActive = false;
  let touchV = 0;
  let touchT = 0;
  let touchPending = 0;
  let touchFrame = 0;
  let flingFrame = 0;
  let flingV = 0;
  let flingT = 0;
  let flingSoftStop = 0;

  const frameBudget = () =>
    Math.min(MAX_FRAME_PX, MAX_FRAME_VH * window.innerHeight);

  const endGesture = () => {
    if (gestureIdleTimer) clearTimeout(gestureIdleTimer);
    gestureIdleTimer = undefined;
    m.gestureLive = false;
    /* Clear spend so the next physical input can advance again. */
    m.lastAdvanceGesture = -1;
  };

  const beginOrContinueGesture = () => {
    /* First tick after idle = new gesture. Continuous momentum keeps the id. */
    if (!gestureIdleTimer) {
      m.gestureId += 1;
    }
    m.gestureLive = true;
    if (gestureIdleTimer) clearTimeout(gestureIdleTimer);
    gestureIdleTimer = setTimeout(endGesture, GESTURE_IDLE_MS);
  };

  const gestureSpent = () =>
    m.lastAdvanceGesture >= 0 && m.gestureId === m.lastAdvanceGesture;

  const inSettleCooldown = () => performance.now() < settleUntil;

  const noteBusyTransition = (settleMs = SETTLE_COOLDOWN_MS) => {
    const busy = spineBusy(m);
    if (wasBusy && !busy) {
      settleUntil = performance.now() + settleMs;
    }
    wasBusy = busy;
    return busy;
  };

  const shouldSwallow = (opts?: {
    ignoreGestureSpend?: boolean;
    settleMs?: number;
  }) =>
    splashBlocking(m) ||
    noteBusyTransition(opts?.settleMs) ||
    inSettleCooldown() ||
    (!opts?.ignoreGestureSpend && gestureSpent());

  const drain = () => {
    frame = 0;
    if (!pending) return;
    if (shouldSwallow()) {
      pending = 0;
      return;
    }
    const budget = frameBudget();
    const step = Math.sign(pending) * Math.min(Math.abs(pending), budget);
    pending -= step;
    applyScroll(m, step);
    if (pending) frame = requestAnimationFrame(drain);
  };

  const enqueue = (dy: number) => {
    if (!dy) return;
    if (shouldSwallow()) {
      pending = 0;
      return;
    }
    const cap = MAX_PENDING_VH * window.innerHeight;
    pending = Math.max(-cap, Math.min(cap, pending + dy));
    if (!frame) frame = requestAnimationFrame(drain);
  };

  const drainTouch = () => {
    touchFrame = 0;
    if (!touchPending) return;
    if (shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
      touchPending = 0;
      return;
    }
    /* One coalesced apply per frame — tracks the finger without mid-event jumps. */
    const step = touchPending;
    touchPending = 0;
    applyScroll(m, step);
  };

  const enqueueTouch = (dy: number) => {
    if (!dy) return;
    if (shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
      touchPending = 0;
      return;
    }
    touchPending += dy;
    if (!touchFrame) touchFrame = requestAnimationFrame(drainTouch);
  };

  const stopFling = () => {
    if (flingFrame) cancelAnimationFrame(flingFrame);
    flingFrame = 0;
    flingV = 0;
    flingSoftStop = 0;
  };

  const stepFling = () => {
    flingFrame = 0;
    if (touchActive) return;
    /*
     * Soft-stop into a gate lock: bleed velocity for a couple frames instead of
     * a hard cut, which read as a hitch on mobile.
     */
    if (shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
      flingSoftStop += 1;
      flingV *= 0.45;
      if (flingSoftStop > 2 || Math.abs(flingV) < FLICK_STOP_V) {
        stopFling();
        endGesture();
        return;
      }
    } else {
      flingSoftStop = 0;
    }
    const now = performance.now();
    const dt = Math.min(32, Math.max(1, now - flingT));
    flingT = now;
    applyScroll(m, flingV * dt);
    flingV *= Math.pow(FLICK_DECAY, dt / 16);
    if (Math.abs(flingV) < FLICK_STOP_V) {
      stopFling();
      endGesture();
      return;
    }
    /* Keep the gesture alive so the idle timer does not split the fling. */
    beginOrContinueGesture();
    flingFrame = requestAnimationFrame(stepFling);
  };

  const startFling = (v: number) => {
    flingV = Math.sign(v) * Math.min(Math.abs(v) * FLICK_GAIN, FLICK_MAX_V);
    flingT = performance.now();
    if (!flingFrame) flingFrame = requestAnimationFrame(stepFling);
  };

  /*
   * Work case gates re-mark the same section id, so one-section spend never
   * fires — a single fling could sail MKIT→…→And more. Expose a hook so
   * swapCase can end the touch gesture when the case actually changes.
   */
  m.consumeTouchAdvance = () => {
    touchPending = 0;
    if (touchFrame) {
      cancelAnimationFrame(touchFrame);
      touchFrame = 0;
    }
    stopFling();
    endGesture();
  };

  const onWheel = (e: WheelEvent) => {
    /* Allow pinch-zoom / browser zoom. */
    if (e.ctrlKey) return;
    if (allowsNativeScroll(e.target)) return;

    /* Seek / rebuild / splash owns the page — swallow input entirely. */
    if (splashBlocking(m)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    /*
     * Swallow without refreshing the idle timer — otherwise a spent gesture
     * never ends while the user keeps wheeling.
     */
    if (shouldSwallow()) {
      pending = 0;
      return;
    }

    beginOrContinueGesture();
    enqueue(wheelDeltaY(e) * WHEEL_GAIN);
  };

  const onTouchStart = (e: TouchEvent) => {
    /* Touch down arrests an in-flight fling, same as native scrolling. */
    stopFling();
    if (allowsNativeScroll(e.target)) {
      touchActive = false;
      return;
    }
    if (splashBlocking(m)) {
      touchActive = false;
      e.preventDefault();
      return;
    }
    if (e.touches.length !== 1) {
      touchActive = false;
      return;
    }
    touchActive = true;
    touchY = e.touches[0].clientY;
    touchV = 0;
    touchT = performance.now();

    /*
     * Touch ignores one-section spend — that gate made mobile feel like
     * 1px scrolling (swipe dies at the first section mark). Still block
     * while splash / section timelines own the page.
     */
    if (shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
      touchPending = 0;
      e.preventDefault();
      return;
    }

    beginOrContinueGesture();
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!touchActive || e.touches.length !== 1) return;
    if (allowsNativeScroll(e.target)) {
      touchActive = false;
      return;
    }
    if (splashBlocking(m)) {
      touchPending = 0;
      e.preventDefault();
      return;
    }

    const y = e.touches[0].clientY;
    const dy = touchY - y;
    touchY = y;

    const now = performance.now();
    const dt = now - touchT;
    touchT = now;
    /* Smoothed release velocity; a long gap means the finger had stopped. */
    if (dt > 200) touchV = 0;
    else if (dt > 0) touchV = touchV * 0.55 + (dy / dt) * 0.45;

    e.preventDefault();

    if (shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
      touchPending = 0;
      return;
    }

    beginOrContinueGesture();
    if (dy) enqueueTouch(dy);
  };

  const onTouchEnd = () => {
    const wasActive = touchActive;
    touchActive = false;
    /* Flush any coalesced finger delta before fling / end. */
    if (touchPending) {
      if (!shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })) {
        applyScroll(m, touchPending);
      }
      touchPending = 0;
      if (touchFrame) cancelAnimationFrame(touchFrame);
      touchFrame = 0;
    }
    const v = touchV;
    touchV = 0;
    if (
      wasActive &&
      Math.abs(v) >= FLICK_MIN_V &&
      performance.now() - touchT < FLICK_RELEASE_MS &&
      !shouldSwallow({ ignoreGestureSpend: true, settleMs: TOUCH_SETTLE_COOLDOWN_MS })
    ) {
      /* Fling keeps the gesture open; stepFling ends it when it settles. */
      startFling(v);
      return;
    }
    /* Finger up ends the gesture immediately — next touch is a new advance. */
    endGesture();
  };

  window.addEventListener("wheel", onWheel, { passive: false, capture: true });
  window.addEventListener("touchstart", onTouchStart, {
    passive: false,
    capture: true,
  });
  window.addEventListener("touchmove", onTouchMove, {
    passive: false,
    capture: true,
  });
  window.addEventListener("touchend", onTouchEnd, {
    passive: true,
    capture: true,
  });
  window.addEventListener("touchcancel", onTouchEnd, {
    passive: true,
    capture: true,
  });

  m.unsub.push(() => {
    window.removeEventListener("wheel", onWheel, true);
    window.removeEventListener("touchstart", onTouchStart, true);
    window.removeEventListener("touchmove", onTouchMove, true);
    window.removeEventListener("touchend", onTouchEnd, true);
    window.removeEventListener("touchcancel", onTouchEnd, true);
    if (frame) cancelAnimationFrame(frame);
    if (touchFrame) cancelAnimationFrame(touchFrame);
    if (flingFrame) cancelAnimationFrame(flingFrame);
    if (gestureIdleTimer) clearTimeout(gestureIdleTimer);
    pending = 0;
    touchPending = 0;
  });
}
